import type { DigDug, DigSetup, DigState } from '@goodtime/shared';
import type { GameView } from './core';

export type DigView = GameView<DigState, DigDug | null>;

/** The treasure map's dig (the story): no bombs, so nothing to bank; every
 *  square holds coins and they add up to the treasure. */
export const noBombs = (setup: DigSetup | undefined): boolean => setup?.bombs === 0;

/** The square at `index`, if it has been dug. */
export const dugAt = (view: DigView, index: number): DigDug | null => view.state?.dug.find((d) => d.index === index) ?? null;

/** Whether tapping square `index` digs it now. */
export const canDig = (view: DigView, index: number): boolean => view.phase === 'play' && !dugAt(view, index);

/** Whether BANK can be pressed: there's a pot to bank (never on a field
 *  with no bombs, which is dug to the end). */
export const canBank = (view: DigView, setup?: DigSetup): boolean =>
  view.phase === 'play' && !noBombs(setup) && (view.state?.pot ?? 0) > 0;

/** BANK's label, with the pot it keeps. */
export const bankLabel = (view: DigView): string => {
  const pot = view.state?.pot ?? 0;
  return pot > 0 ? `BANK ${pot.toLocaleString('en-GB')}` : 'BANK';
};

/** Safe digs made: the coin squares uncovered. */
export const safeDigs = (view: DigView): number => view.state?.dug.filter((d) => d.kind === 'coins').length ?? 0;

/** Whether the dig ended on a bomb (a loss, whatever it pays). */
export const hitBomb = (view: DigView): boolean => view.state?.ended === 'bomb';

/** The line under the title. */
export function digLead(view: DigView, setup: DigSetup | undefined): string {
  if (view.phase === 'over') {
    const ended = view.state?.ended;
    if (ended === 'bomb') {
      return safeDigs(view) > 0 && (setup?.perDig ?? 0) > 0 ? 'A bomb! The pot is lost.' : 'A bomb on the first dig. No coins this time.';
    }
    if (ended === 'cleared') return noBombs(setup) ? 'The treasure is yours!' : 'Every coin dug up!';
    return 'Banked!';
  }
  if (noBombs(setup)) return 'No bombs here: every square holds coins. Dig them all up!';
  return 'Dig for coins. Bank them before you hit a bomb.';
}

/** Under what a bomb still pays: why, before where it went. */
export const bombNote = (setup: DigSetup | undefined, where: string): string =>
  `${setup?.perDig ?? 0} coins for each safe dig. ${where}`;

/** The counters under the field. */
export const digCounters = (view: DigView): { dug: number; pot: number } => ({
  dug: view.state?.dug.length ?? 0,
  pot: view.state?.pot ?? 0,
});

/** What things mean, said on a tap. */
export const DIG_EXPLAIN = {
  counters: (setup: DigSetup | undefined) =>
    noBombs(setup)
      ? `Every square hides coins, and there are no bombs. Pot is the coins you've dug up so far: dig every square for the whole treasure.`
      : `Each square hides coins or one of ${setup?.bombs ?? 3} bombs. Pot is the coins you've dug up, and BANK keeps them. ` +
        `A bomb loses the pot: you keep ${setup?.perDig ?? 0} coins for each safe dig.`,
  square: (coins: number) => `This square held ${coins.toLocaleString('en-GB')} coins. They're in the pot.`,
};
